import {
	type UseFloatingOptions,
	autoUpdate,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
	useRole,
} from "@floating-ui/react";
import {
	type Dispatch,
	type SetStateAction,
	useCallback,
	useState,
} from "react";
import { boolNegate } from "../utils";
import { useFocusRestoration } from "./use-focus-restoration";

export type UsePopoverProps = UseFloatingOptions & {
	open?: boolean;
	setOpen?: Dispatch<SetStateAction<boolean>>;
};

export type UsePopoverState = ReturnType<typeof usePopover>;

export function usePopover(props: UsePopoverProps = {}) {
	const [uncontrolledOpen, setUncontrolledOpen] = useState(props.open ?? false);
	const open = props.open ?? uncontrolledOpen;
	const setOpen = props.setOpen ?? setUncontrolledOpen;

	const toggleOpen = useCallback(() => {
		setOpen(boolNegate);
	}, [setOpen]);

	const floating = useFloating({
		...props,
		open: open,
		onOpenChange: setOpen,
		whileElementsMounted: autoUpdate,
	});

	const { context } = floating;
	const click = useClick(context, { enabled: open });
	const dismiss = useDismiss(context);
	const role = useRole(context);
	const interactions = useInteractions([click, dismiss, role]);

	useFocusRestoration(open);

	return {
		open,
		setOpen,
		toggleOpen,
		floating,
		interactions,
	};
}
